/**
 * Hook: GET /api/t/{slug}/jds/{id} — single JD fetch.
 *
 * Cache key: ``['jd', tenantSlug, jdId]``. Used by JdEdit + JdDetail pages.
 * Mirrors useJds shape (single object instead of envelope).
 *
 * Phase 2.3 additions (jd-rebuild-spec.md §2 + §6):
 *   - `status: JdStatus` — Draft/Active/Paused/Archived lifecycle.
 *   - `parsed_fields: JdParsedFields | null` — AI-extracted structured fields.
 *   - `parse_status: JdParseStatus | null` — last parse-pass outcome surfaced
 *     for the Parsed details panel.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import type { PriorityAttribute } from "@/components/jds/priorityCriteria";
import type { InterviewRound } from "@/components/jds/interviewRounds";

/** JD lifecycle status — locked enum per jd-rebuild-spec.md §2.1. */
export type JdStatus = "draft" | "active" | "paused" | "archived";

/** Possible parse-pass outcome states. */
export type JdParseStatus = "idle" | "parsing" | "parsed" | "parse_failed";

/**
 * AI-parsed structured fields surfaced on JD Detail per spec §4.5.
 * All fields nullable — extraction may partially succeed.
 */
export interface JdParsedFields {
  title: string | null;
  seniority: string | null;
  location: string | null;
  employment_type: string | null;
  comp_band: string | null;
  requirements: string[] | null;
  nice_to_haves: string[] | null;
  company_description: string | null;
}

export interface Jd {
  id: string;
  tenant_id: string;
  created_by: string;
  name: string;
  filename: string | null;
  text: string | null;
  company_profile_override: string | null;
  is_sample: boolean;
  storage_key_present: boolean;
  /** JD lifecycle status. Defaults to `active` per spec §2. */
  status: JdStatus;
  /** AI-parsed structured fields. Null when parse hasn't run / failed entirely. */
  parsed_fields: JdParsedFields | null;
  /** Last parse-pass outcome. Null on records pre-parse rollout. */
  parse_status: JdParseStatus | null;
  /**
   * Per-JD priority criteria (Concept B), in priority order. Null/absent on
   * JDs created before the feature or with no criteria set.
   */
  priority_attributes?: PriorityAttribute[] | null;
  /**
   * Ordered, named interview rounds for this role (backend
   * `jds.interview_rounds`, PR #574). Each round carries a server-minted
   * `id` and a `name`. Null/absent on JDs created before the feature.
   */
  interview_rounds?: InterviewRound[] | null;
  created_at: string;
  updated_at: string;
  /**
   * D9 — number of candidates in this JD's pipeline (distinct candidates
   * paired via `candidate_jd_pairings`). Returned by the JD LIST endpoint
   * (Manage Jobs "Candidates" column); absent/null on the single-JD GET. Null
   * → the table renders the "—" fallback.
   */
  candidate_count?: number | null;
}

export function useJd(jdId: string | undefined): UseQueryResult<Jd, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  return useQuery<Jd, ApiError>({
    queryKey: ["jd", tenantSlug, jdId],
    queryFn: async () => {
      const r = await apiClient.get<Jd>(`/api/t/${tenantSlug}/jds/${jdId}`);
      return r.data;
    },
    enabled: !!tenantSlug && !!jdId,
    staleTime: 30_000,
  });
}
